import html2canvas from 'html2canvas';
import type { SnapCapturePolicy, SnapPrivacyRedactionPolicy } from '../types';
import {
  hideExcludedElements,
  isExcludedElement,
  isHeavyCaptureElement,
  restoreExcludedElements
} from '../redaction/neverCapture';
import { SnapDenyCodes } from '../types';
import { createSnapError } from '../security/sanitize';

export const MAX_CAPTURE_DIM = 7680;
export const MAX_EDITOR_EDGE = 1920;
export const MAX_ENCODED_BYTES = 20 * 1024 * 1024;
export const CAPTURE_TIMEOUT_MS = 20000;

export interface CaptureResult {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  redacted: boolean;
  clientCaptureId: string;
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(createSnapError('capture_failed', SnapDenyCodes.CaptureFailed, message));
    }, ms);
    promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function downscale(source: HTMLCanvasElement): HTMLCanvasElement {
  const maxEdge = Math.max(source.width, source.height);
  if (maxEdge <= MAX_EDITOR_EDGE) {
    const c = document.createElement('canvas');
    c.width = source.width;
    c.height = source.height;
    c.getContext('2d')!.drawImage(source, 0, 0);
    return c;
  }
  const scale = MAX_EDITOR_EDGE / maxEdge;
  const w = Math.max(1, Math.round(source.width * scale));
  const h = Math.max(1, Math.round(source.height * scale));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d')!.drawImage(source, 0, 0, w, h);
  return c;
}

function newCaptureId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cap-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Screenshot decision (Track G): Option A — reuse html2canvas@1.4.1 as an explicit
 * package dependency (same major as Panel vendor). Bundled into ESM/UMD builds.
 * No runtime CDN fetch. CSP: consumers must allow blob:/data: for canvas export
 * and same-origin DOM access; cross-origin images require CORS ACAO.
 */
export async function captureViewport(args: {
  capturePolicy: SnapCapturePolicy;
  privacyPolicy: SnapPrivacyRedactionPolicy;
  userInitiated: boolean;
  signal?: AbortSignal;
}): Promise<CaptureResult> {
  if (args.capturePolicy.requiresUserInitiation && !args.userInitiated) {
    throw createSnapError(
      'capture_failed',
      SnapDenyCodes.CaptureFailed,
      'Screenshot capture requires explicit user initiation.'
    );
  }

  if (!args.capturePolicy.screenshotEnabled) {
    throw createSnapError(
      'capture_disabled',
      SnapDenyCodes.CaptureDisabled,
      'Screenshot capture is disabled by server policy.'
    );
  }

  if (args.signal?.aborted) {
    throw createSnapError(
      'cancelled',
      SnapDenyCodes.Cancelled,
      'Capture cancelled.',
      { retryable: false }
    );
  }

  let restored = hideExcludedElements(args.privacyPolicy);
  try {
    const canvas = await withTimeout(
      html2canvas(document.body, {
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        useCORS: true,
        logging: false,
        imageTimeout: 5000,
        ignoreElements: (el) =>
          isExcludedElement(el, args.privacyPolicy) || isHeavyCaptureElement(el)
      }),
      CAPTURE_TIMEOUT_MS,
      'Viewport capture timed out.'
    );

    if (args.signal?.aborted) {
      throw createSnapError(
        'cancelled',
        SnapDenyCodes.Cancelled,
        'Capture cancelled.'
      );
    }

    if (!canvas.width || !canvas.height) {
      throw createSnapError(
        'capture_failed',
        SnapDenyCodes.CaptureFailed,
        'Capture produced an empty image.'
      );
    }
    if (canvas.width > MAX_CAPTURE_DIM || canvas.height > MAX_CAPTURE_DIM) {
      throw createSnapError(
        'capture_failed',
        SnapDenyCodes.CaptureFailed,
        'Captured image exceeds maximum dimensions.'
      );
    }

    const editor = downscale(canvas);
    return {
      canvas: editor,
      width: editor.width,
      height: editor.height,
      redacted: true,
      clientCaptureId: newCaptureId()
    };
  } finally {
    restoreExcludedElements(restored);
    restored = [];
  }
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(
            createSnapError(
              'capture_failed',
              SnapDenyCodes.CaptureFailed,
              'Failed to encode screenshot.'
            )
          );
          return;
        }
        if (blob.size > MAX_ENCODED_BYTES) {
          reject(
            createSnapError(
              'capture_failed',
              SnapDenyCodes.CaptureFailed,
              'Screenshot exceeds maximum encoded size.'
            )
          );
          return;
        }
        resolve(blob);
      },
      'image/png',
      0.92
    );
  });
}
